import {
  Users,
  UserPlus,
  Mail,
  Shield,
  Search,
  Trash2,
  X,
  AlertCircle,
  Edit2,
  Key,
  Check,
  RefreshCw,
  Ban,
  UserCheck,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SHELL_EVENTS, publishShellEvent } from '../../lib/shellEvents';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

import {
  czlonkowieZOdpowiedzi,
  etykietaCzlonka,
  filtrujCzlonkow,
  inicjalCzlonka,
  type OrganizationMember,
  type WierszCzlonka,
} from './czlonkowie';

// Helper to invoke edge functions with a guaranteed-fresh access token.
// getSession() reads from localStorage cache and can return expired tokens
// (Supabase JS SDK v2.39+). refreshSession() always round-trips to the
// auth server so we never send a stale JWT to the edge function gateway.
async function invokeWithAuth(functionName: string, body: Record<string, unknown>) {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.refreshSession();

  if (sessionError || !session?.access_token) {
    console.error('[invokeWithAuth] Session refresh failed', sessionError);
    return { data: null, error: { message: 'No active session' } };
  }

  return supabase.functions.invoke(functionName, {
    body,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
}

export function UsersPage() {
  const { t } = useTranslation();
  const { currentOrganization, hasRole } = useAuth();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Edit user state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState<OrganizationMember | null>(null);
  const [editFullName, setEditFullName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Password reset state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordMember, setResetPasswordMember] = useState<OrganizationMember | null>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);

  // Success messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Ban user state
  const [showBanModal, setShowBanModal] = useState(false);
  const [banMember, setBanMember] = useState<OrganizationMember | null>(null);
  const [banDuration, setBanDuration] = useState('permanent');
  const [isBanning, setIsBanning] = useState(false);

  // Role change state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleChangeMember, setRoleChangeMember] = useState<OrganizationMember | null>(null);
  const [newRole, setNewRole] = useState('member');
  const [isChangingRole, setIsChangingRole] = useState(false);

  const canManageUsers = hasRole(['owner', 'admin']);
  const isOwner = hasRole(['owner']);

  useEffect(() => {
    if (currentOrganization) {
      fetchMembers();
      fetchInvitations();
    }
  }, [currentOrganization]);

  const fetchMembers = async () => {
    if (!currentOrganization) return;

    try {
      // RPC z 679, nie join z tabelą `users` po kluczu obcym: RLS na `users`
      // oddaje właścicielowi tylko JEGO wiersz, więc join zwracał `user: null`
      // dla każdego innego członka i ekran padał na pierwszym `.email` —
      // patrz `czlonkowie.ts`.
      const { data, error } = await supabase.rpc('czlonkowie_organizacji', {
        p_organization_id: currentOrganization.organization_id,
      });

      if (error) throw error;
      const wiersze = ((data ?? []) as WierszCzlonka[]).filter((w) => w.status === 'active');
      setMembers(czlonkowieZOdpowiedzi(wiersze));
    } catch (err) {
      console.error('Error fetching members:', err);
      setError('Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvitations = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('organization_invitations')
        .select('*')
        .eq('organization_id', currentOrganization.organization_id)
        .eq('status', 'pending');

      if (error) throw error;
      setInvitations(data || []);
    } catch (err) {
      console.error('Error fetching invitations:', err);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization || !inviteEmail) return;

    setIsInviting(true);
    setInviteError(null);

    try {
      // Check if user already exists in organization
      const existingMember = members.find((m) => m.user.email === inviteEmail);
      if (existingMember) {
        setInviteError(
          t('users.errors.alreadyMember', 'This user is already a member of the organization')
        );
        setIsInviting(false);
        return;
      }

      // Check if invitation already exists
      const existingInvite = invitations.find((i) => i.email === inviteEmail);
      if (existingInvite) {
        setInviteError(
          t('users.errors.alreadyInvited', 'An invitation has already been sent to this email')
        );
        setIsInviting(false);
        return;
      }

      // Try primary method: send-invitation edge function
      let success = false;
      let errorMessage = '';

      try {
        const { data, error } = await invokeWithAuth('send-invitation', {
          email: inviteEmail,
          organization_id: currentOrganization.organization_id,
          organization_name: currentOrganization.organization?.name || 'Your Organization',
          role: inviteRole,
          redirect_url: window.location.origin,
        });

        if (!error && !data?.error) {
          success = true;
        } else {
          errorMessage = data?.error || error?.message || '';
        }
      } catch (err) {
        console.error('Primary invitation method failed:', err);
        errorMessage = err instanceof Error ? err.message : 'Edge function error';
      }

      // Fallback: use admin-user-actions edge function
      if (!success) {
        try {
          const { data, error } = await invokeWithAuth('admin-user-actions', {
            action: 'create_invitation',
            email: inviteEmail,
            organization_id: currentOrganization.organization_id,
            organization_name: currentOrganization.organization?.name || 'Your Organization',
            role: inviteRole,
            redirect_url: window.location.origin,
          });

          if (!error && data?.success) {
            success = true;
            if (!data.email_sent) {
              const emailErrorDetail = data.email_error ? ` (${data.email_error})` : '';
              setSuccessMessage(
                t(
                  'users.invitationCreatedNoEmail',
                  'Invitation created. Email could not be sent - please notify the user manually.'
                ) + emailErrorDetail
              );
            }
          } else {
            errorMessage = data?.error || error?.message || errorMessage;
          }
        } catch (err) {
          console.error('Fallback invitation method failed:', err);
        }
      }

      // Final fallback: create invitation directly in database
      if (!success) {
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();

          const { error: dbError } = await supabase.from('organization_invitations').insert({
            organization_id: currentOrganization.organization_id,
            email: inviteEmail,
            role: inviteRole,
            status: 'pending',
            invited_by: user?.id,
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          });

          if (!dbError) {
            success = true;
            setSuccessMessage(
              t(
                'users.invitationCreatedNoEmail',
                'Invitation created. Email could not be sent - please notify the user manually.'
              )
            );
          } else {
            errorMessage = dbError.message;
          }
        } catch (err) {
          console.error('Direct database invitation failed:', err);
          errorMessage = err instanceof Error ? err.message : 'Failed to create invitation';
        }
      }

      if (success) {
        // Role only — the invitee's e-mail is PII and must not enter organism memory.
        void publishShellEvent(SHELL_EVENTS.memberInvited, { role: inviteRole });
        await fetchInvitations();
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteRole('member');
      } else {
        setInviteError(
          t('users.errors.inviteFailed', 'Failed to send invitation: {{error}}', {
            error: errorMessage,
          })
        );
      }
    } catch (err) {
      console.error('Error sending invitation:', err);
      setInviteError(
        err instanceof Error
          ? err.message
          : t('users.errors.inviteFailed', 'Failed to send invitation')
      );
    } finally {
      setIsInviting(false);
    }
  };

  const handleEditUser = (member: OrganizationMember) => {
    setEditingMember(member);
    setEditFullName(member.user.full_name || '');
    setEditError(null);
    setShowEditModal(true);
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMember || !currentOrganization) return;

    setIsUpdating(true);
    setEditError(null);

    try {
      // Try edge function first
      const { data, error } = await invokeWithAuth('admin-user-actions', {
        action: 'update_user',
        user_id: editingMember.user_id,
        organization_id: currentOrganization.organization_id,
        full_name: editFullName,
      });

      if (error || data?.error) {
        // Fallback: update directly in database
        const { error: dbError } = await supabase
          .from('users')
          .update({ full_name: editFullName })
          .eq('id', editingMember.user_id);

        if (dbError) throw dbError;
      }

      await fetchMembers();
      setShowEditModal(false);
      setEditingMember(null);
      setSuccessMessage(t('users.userUpdated', 'User updated successfully'));
    } catch (err) {
      console.error('Error updating user:', err);
      setEditError(
        err instanceof Error ? err.message : t('users.errors.updateFailed', 'Failed to update user')
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResetPassword = (member: OrganizationMember) => {
    setResetPasswordMember(member);
    setResetPasswordSuccess(false);
    setShowResetPasswordModal(true);
  };

  const handleSendPasswordReset = async () => {
    if (!resetPasswordMember || !currentOrganization) return;

    setIsResettingPassword(true);

    try {
      // Try edge function first
      const { data, error } = await invokeWithAuth('admin-user-actions', {
        action: 'reset_password',
        email: resetPasswordMember.user.email,
        organization_id: currentOrganization.organization_id,
        redirect_url: window.location.origin,
      });

      if (error || data?.error) {
        // Fallback: use standard Supabase auth
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(
          resetPasswordMember.user.email,
          { redirectTo: `${window.location.origin}/reset-password` }
        );

        if (resetError) throw resetError;
      }

      setResetPasswordSuccess(true);
    } catch (err) {
      console.error('Error sending password reset:', err);
      setError(
        err instanceof Error
          ? err.message
          : t('users.errors.resetFailed', 'Failed to send password reset email')
      );
      setShowResetPasswordModal(false);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('organization_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;
      await fetchInvitations();
    } catch (err) {
      console.error('Error canceling invitation:', err);
    }
  };

  const handleRemoveMember = async (memberId: string, userId: string) => {
    if (!currentOrganization) return;

    // Don't allow removing yourself
    if (userId === currentOrganization.user_id) {
      setError('You cannot remove yourself from the organization');
      return;
    }

    try {
      const { error } = await supabase
        .from('user_organizations')
        .update({ status: 'removed' })
        .eq('id', memberId);

      if (error) throw error;
      void publishShellEvent(SHELL_EVENTS.memberRemoved, {
        member_id: memberId,
        user_id: userId,
      });
      await fetchMembers();
    } catch (err) {
      console.error('Error removing member:', err);
      setError('Failed to remove member');
    }
  };

  const handleBanUser = (member: OrganizationMember) => {
    setBanMember(member);
    setBanDuration('permanent');
    setShowBanModal(true);
  };

  const handleConfirmBan = async () => {
    if (!banMember || !currentOrganization) return;

    setIsBanning(true);
    try {
      const { data, error } = await invokeWithAuth('admin-user-actions', {
        action: 'ban_user',
        user_id: banMember.user_id,
        organization_id: currentOrganization.organization_id,
        ban_duration: banDuration,
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Failed to ban user');
      }

      void publishShellEvent(SHELL_EVENTS.memberBanned, {
        user_id: banMember.user_id,
        ban_duration: banDuration,
      });
      await fetchMembers();
      setShowBanModal(false);
      setBanMember(null);
      setSuccessMessage(t('users.userBanned', 'User has been banned'));
    } catch (err) {
      console.error('Error banning user:', err);
      setError(
        err instanceof Error ? err.message : t('users.errors.banFailed', 'Failed to ban user')
      );
    } finally {
      setIsBanning(false);
    }
  };

  const handleUnbanUser = async (member: OrganizationMember) => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await invokeWithAuth('admin-user-actions', {
        action: 'unban_user',
        user_id: member.user_id,
        organization_id: currentOrganization.organization_id,
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Failed to unban user');
      }

      void publishShellEvent(SHELL_EVENTS.memberUnbanned, { user_id: member.user_id });
      await fetchMembers();
      setSuccessMessage(t('users.userUnbanned', 'User has been unbanned'));
    } catch (err) {
      console.error('Error unbanning user:', err);
      setError(
        err instanceof Error ? err.message : t('users.errors.unbanFailed', 'Failed to unban user')
      );
    }
  };

  const handleChangeRole = (member: OrganizationMember) => {
    setRoleChangeMember(member);
    setNewRole(member.role);
    setShowRoleModal(true);
  };

  const handleConfirmRoleChange = async () => {
    if (!roleChangeMember || !currentOrganization) return;

    setIsChangingRole(true);
    try {
      const { data, error } = await invokeWithAuth('admin-user-actions', {
        action: 'change_role',
        user_id: roleChangeMember.user_id,
        organization_id: currentOrganization.organization_id,
        role: newRole,
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Failed to change role');
      }

      void publishShellEvent(SHELL_EVENTS.memberRoleChanged, {
        user_id: roleChangeMember.user_id,
        previous_role: roleChangeMember.role,
        role: newRole,
      });
      await fetchMembers();
      setShowRoleModal(false);
      setRoleChangeMember(null);
      setSuccessMessage(t('users.roleChanged', 'User role has been changed'));
    } catch (err) {
      console.error('Error changing role:', err);
      setError(
        err instanceof Error
          ? err.message
          : t('users.errors.roleChangeFailed', 'Failed to change user role')
      );
    } finally {
      setIsChangingRole(false);
    }
  };

  const filteredMembers = filtrujCzlonkow(members, searchQuery);

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-500/20 text-purple-400';
      case 'admin':
        return 'bg-blue-500/20 text-blue-400';
      case 'manager':
        return 'bg-green-500/20 text-green-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (isLoading) {
    // Szkielet odwzorowuje docelowy układ (pagehead → search → tablecard) — bez skoku layoutu.
    return (
      <div className="space-y-6">
        <div className="skel h-9 w-56" />
        <div className="skel h-11 w-full" />
        <div className="skel h-64 w-full rounded-ids-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <Users className="w-6 h-6 text-aurora-brand" />
            {t('users.title', 'Team Members')}
          </h1>
          <p className="pagehead__sub">
            {t('users.subtitle', "Manage your organization's team members and their roles")}
          </p>
        </div>
        {canManageUsers && (
          <div className="pagehead__actions">
            <button onClick={() => setShowInviteModal(true)} className="btn btn--primary">
              <UserPlus className="w-5 h-5" />
              {t('users.invite', 'Invite Member')}
            </button>
          </div>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="flex items-center gap-2 p-4 bg-green-500/20 border border-green-500/30 rounded-ids-md text-green-400">
          <Check className="w-5 h-5" />
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/20 border border-red-500/30 rounded-ids-md text-red-400">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="inputwrap">
        <Search />
        <input
          type="text"
          placeholder={t('users.search', 'Search members...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input"
        />
      </div>

      {/* Members Table */}
      <div className="card tablecard">
        <table className="table">
          <thead>
            <tr>
              <th>{t('users.member', 'Member')}</th>
              <th>{t('users.role', 'Role')}</th>
              <th>{t('users.joined', 'Joined')}</th>
              {canManageUsers && <th className="text-right">{t('users.actions', 'Actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map((member) => (
              <tr key={member.id}>
                <td>
                  <div className="cellname">
                    <span className="avatar">{inicjalCzlonka(member.user)}</span>
                    <div>
                      <p className="text-aurora-text font-medium">
                        {etykietaCzlonka(member.user).nazwa}
                      </p>
                      <p className="text-sm font-normal text-aurora-muted">
                        {etykietaCzlonka(member.user).email}
                      </p>
                    </div>
                  </div>
                </td>
                <td>
                  {canManageUsers && member.role !== 'owner' ? (
                    <button
                      onClick={() => handleChangeRole(member)}
                      className={`badge ${getRoleBadgeColor(member.role)} border-transparent cursor-pointer hover:opacity-80 transition-opacity`}
                    >
                      <Shield className="w-3 h-3" />
                      {t(`roles.${member.role}`, member.role)}
                    </button>
                  ) : (
                    <span className={`badge ${getRoleBadgeColor(member.role)} border-transparent`}>
                      <Shield className="w-3 h-3" />
                      {t(`roles.${member.role}`, member.role)}
                    </span>
                  )}
                </td>
                <td className="text-sm text-aurora-muted mono">
                  {new Date(member.created_at).toLocaleDateString()}
                </td>
                {canManageUsers && (
                  <td className="text-right">
                    {/* .rowact — akcje odsłaniane na hover/focus-within wiersza (na dotyku zawsze widoczne) */}
                    <div className="rowact flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEditUser(member)}
                        className="iconbtn w-8 h-8"
                        title={t('users.edit', 'Edit user')}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(member)}
                        className="iconbtn w-8 h-8 hover:text-aurora-warning"
                        title={t('users.resetPassword', 'Reset password')}
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      {member.role !== 'owner' &&
                        member.user_id !== currentOrganization?.user_id && (
                          <>
                            <button
                              onClick={() => handleChangeRole(member)}
                              className="iconbtn w-8 h-8"
                              title={t('users.changeRole', 'Change role')}
                            >
                              <Shield className="w-4 h-4" />
                            </button>
                            {isOwner && member.status !== 'suspended' && (
                              <button
                                onClick={() => handleBanUser(member)}
                                className="iconbtn w-8 h-8 hover:text-aurora-danger hover:bg-aurora-danger/15"
                                title={t('users.ban', 'Ban user')}
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            )}
                            {isOwner && member.status === 'suspended' && (
                              <button
                                onClick={() => handleUnbanUser(member)}
                                className="iconbtn w-8 h-8"
                                title={t('users.unban', 'Unban user')}
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleRemoveMember(member.id, member.user_id)}
                              className="iconbtn w-8 h-8 hover:text-aurora-danger hover:bg-aurora-danger/15"
                              title={t('users.remove', 'Remove member')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {filteredMembers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-aurora-subtle mx-auto mb-4" />
            <h3 className="display text-lg font-medium text-aurora-text mb-2">
              {searchQuery
                ? t('users.noResults', 'No members found')
                : t('users.noMembers', 'No team members yet')}
            </h3>
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="card">
          <div className="card__head">
            <Mail className="w-5 h-5 text-yellow-400" />
            <h2 className="card__title">{t('users.pendingInvitations', 'Pending Invitations')}</h2>
            <span className="badge ml-auto">{invitations.length}</span>
          </div>
          <div className="divide-y divide-aurora-border">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between gap-3 px-6 py-4"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-aurora-text break-all">{invitation.email}</p>
                    <p className="text-sm text-aurora-muted">
                      {t(`roles.${invitation.role}`, invitation.role)} •{' '}
                      {t('users.expires', 'Expires')}:{' '}
                      {new Date(invitation.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                {canManageUsers && (
                  <button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    className="iconbtn w-8 h-8 text-red-400 hover:bg-red-500/20"
                    title={t('users.cancelInvitation', 'Cancel invitation')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-aurora-scrim backdrop-blur-[3px]"
            onClick={() => setShowInviteModal(false)}
          />
          <div className="relative bg-aurora-surface rounded-ids-xl border border-aurora-border shadow-elev-4 w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <div className="modal__head">
              <h3 className="modal__title">{t('users.inviteMember', 'Invite Team Member')}</h3>
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className="iconbtn ml-auto"
                aria-label={t('common.close', 'Close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="modal__body space-y-4">
                <div>
                  <label className="field__label block mb-1">
                    {t('users.email', 'Email Address')}
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    className="input"
                    placeholder="colleague@company.com"
                  />
                </div>
                <div>
                  <label className="field__label block mb-1">{t('users.role', 'Role')}</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="select"
                  >
                    <option value="member">{t('roles.member', 'Member')}</option>
                    <option value="manager">{t('roles.manager', 'Manager')}</option>
                    <option value="admin">{t('roles.admin', 'Admin')}</option>
                  </select>
                </div>
                {inviteError && <p className="text-sm text-aurora-danger">{inviteError}</p>}
              </div>
              <div className="modal__foot">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="btn btn--ghost"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button type="submit" disabled={isInviting} className="btn btn--primary">
                  {isInviting
                    ? t('common.sending', 'Sending...')
                    : t('users.sendInvite', 'Send Invite')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-aurora-scrim backdrop-blur-[3px]"
            onClick={() => setShowEditModal(false)}
          />
          <div className="relative bg-aurora-surface rounded-ids-xl border border-aurora-border shadow-elev-4 w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <div className="modal__head">
              <Edit2 className="w-5 h-5 text-aurora-info" />
              <h3 className="modal__title">{t('users.editUser', 'Edit User')}</h3>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="iconbtn ml-auto"
                aria-label={t('common.close', 'Close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveUser}>
              <div className="modal__body space-y-4">
                <div>
                  <label className="field__label block mb-1">
                    {t('users.email', 'Email Address')}
                  </label>
                  <input
                    type="email"
                    value={editingMember.user.email}
                    disabled
                    className="input opacity-60 cursor-not-allowed"
                  />
                  <p className="text-xs text-aurora-subtle mt-1">
                    {t('users.emailCannotChange', 'Email cannot be changed')}
                  </p>
                </div>
                <div>
                  <label className="field__label block mb-1">
                    {t('users.fullName', 'Full Name')}
                  </label>
                  <input
                    type="text"
                    value={editFullName}
                    onChange={(e) => setEditFullName(e.target.value)}
                    required
                    className="input"
                    placeholder={t('users.enterFullName', 'Enter full name')}
                  />
                </div>
                {editError && <p className="text-sm text-aurora-danger">{editError}</p>}
              </div>
              <div className="modal__foot">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn btn--ghost"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button type="submit" disabled={isUpdating} className="btn btn--primary">
                  {isUpdating ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      {t('common.saving', 'Saving...')}
                    </>
                  ) : (
                    t('common.save', 'Save')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-aurora-scrim backdrop-blur-[3px]"
            onClick={() => setShowResetPasswordModal(false)}
          />
          <div className="relative bg-aurora-surface rounded-ids-xl border border-aurora-border shadow-elev-4 w-full max-w-xl max-h-[92vh] overflow-y-auto">
            {!resetPasswordSuccess ? (
              <>
                <div className="modal__head">
                  <Key className="w-5 h-5 text-aurora-warning" />
                  <h3 className="modal__title">
                    {t('users.resetPasswordTitle', 'Reset Password')}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowResetPasswordModal(false)}
                    className="iconbtn ml-auto"
                    aria-label={t('common.close', 'Close')}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="modal__body space-y-4">
                  <p className="text-aurora-muted">
                    {t(
                      'users.resetPasswordConfirm',
                      'Are you sure you want to send a password reset email to:'
                    )}
                  </p>
                  <div className="p-4 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md">
                    <p className="text-aurora-text font-medium">
                      {resetPasswordMember.user.full_name || resetPasswordMember.user.email}
                    </p>
                    <p className="text-sm text-aurora-muted">{resetPasswordMember.user.email}</p>
                  </div>
                  <p className="text-sm text-aurora-muted">
                    {t(
                      'users.resetPasswordNote',
                      'The user will receive an email with instructions to reset their password.'
                    )}
                  </p>
                </div>
                <div className="modal__foot">
                  <button
                    type="button"
                    onClick={() => setShowResetPasswordModal(false)}
                    className="btn btn--ghost"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleSendPasswordReset}
                    disabled={isResettingPassword}
                    className="btn btn--primary"
                  >
                    {isResettingPassword ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        {t('common.sending', 'Sending...')}
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        {t('users.sendResetEmail', 'Send Reset Email')}
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="modal__body">
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-aurora-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-aurora-success" />
                  </div>
                  <h3 className="display text-lg font-semibold text-aurora-text mb-2">
                    {t('users.resetEmailSent', 'Reset Email Sent')}
                  </h3>
                  <p className="text-aurora-muted mb-6">
                    {t(
                      'users.resetEmailSentDesc',
                      'A password reset email has been sent to {{email}}',
                      { email: resetPasswordMember.user.email }
                    )}
                  </p>
                  <button
                    onClick={() => setShowResetPasswordModal(false)}
                    className="btn btn--secondary"
                  >
                    {t('common.close', 'Close')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ban User Modal */}
      {showBanModal && banMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-aurora-scrim backdrop-blur-[3px]"
            onClick={() => setShowBanModal(false)}
          />
          <div className="relative bg-aurora-surface rounded-ids-xl border border-aurora-border shadow-elev-4 w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <div className="modal__head">
              <Ban className="w-5 h-5 text-aurora-warning" />
              <h3 className="modal__title">{t('users.banUserTitle', 'Ban User')}</h3>
              <button
                type="button"
                onClick={() => setShowBanModal(false)}
                className="iconbtn ml-auto"
                aria-label={t('common.close', 'Close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal__body space-y-4">
              <p className="text-aurora-muted">
                {t('users.banUserConfirm', 'Are you sure you want to ban this user?')}
              </p>
              <div className="p-4 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md">
                <p className="text-aurora-text font-medium">
                  {banMember.user.full_name || banMember.user.email}
                </p>
                <p className="text-sm text-aurora-muted">{banMember.user.email}</p>
              </div>
              <div>
                <label className="field__label block mb-2">
                  {t('users.banDuration', 'Ban Duration')}
                </label>
                <select
                  value={banDuration}
                  onChange={(e) => setBanDuration(e.target.value)}
                  className="select"
                >
                  <option value="1h">{t('users.banDuration1h', '1 hour')}</option>
                  <option value="24h">{t('users.banDuration24h', '24 hours')}</option>
                  <option value="7d">{t('users.banDuration7d', '7 days')}</option>
                  <option value="30d">{t('users.banDuration30d', '30 days')}</option>
                  <option value="permanent">{t('users.banDurationPermanent', 'Permanent')}</option>
                </select>
              </div>
              <p className="text-sm text-aurora-warning">
                {t(
                  'users.banWarning',
                  'The user will not be able to log in until the ban is lifted.'
                )}
              </p>
            </div>
            <div className="modal__foot">
              <button
                type="button"
                onClick={() => setShowBanModal(false)}
                className="btn btn--ghost"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button onClick={handleConfirmBan} disabled={isBanning} className="btn btn--danger">
                {isBanning ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {t('common.processing', 'Processing...')}
                  </>
                ) : (
                  <>
                    <Ban className="w-4 h-4" />
                    {t('users.confirmBan', 'Ban User')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && roleChangeMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-aurora-scrim backdrop-blur-[3px]"
            onClick={() => setShowRoleModal(false)}
          />
          <div className="relative bg-aurora-surface rounded-ids-xl border border-aurora-border shadow-elev-4 w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <div className="modal__head">
              <Shield className="w-5 h-5 text-aurora-brand" />
              <h3 className="modal__title">{t('users.changeRoleTitle', 'Change User Role')}</h3>
              <button
                type="button"
                onClick={() => setShowRoleModal(false)}
                className="iconbtn ml-auto"
                aria-label={t('common.close', 'Close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal__body space-y-4">
              <div className="p-4 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md">
                <p className="text-aurora-text font-medium">
                  {roleChangeMember.user.full_name || roleChangeMember.user.email}
                </p>
                <p className="text-sm text-aurora-muted">{roleChangeMember.user.email}</p>
                <p className="text-sm text-aurora-subtle mt-1">
                  {t('users.currentRole', 'Current role')}:{' '}
                  {t(`roles.${roleChangeMember.role}`, roleChangeMember.role)}
                </p>
              </div>
              <div>
                <label className="field__label block mb-2">{t('users.newRole', 'New Role')}</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="select"
                >
                  {isOwner && <option value="admin">{t('roles.admin', 'Admin')}</option>}
                  <option value="manager">{t('roles.manager', 'Manager')}</option>
                  <option value="member">{t('roles.member', 'Member')}</option>
                  <option value="employee">{t('roles.employee', 'Employee')}</option>
                </select>
              </div>
            </div>
            <div className="modal__foot">
              <button
                type="button"
                onClick={() => setShowRoleModal(false)}
                className="btn btn--ghost"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleConfirmRoleChange}
                disabled={isChangingRole || newRole === roleChangeMember.role}
                className="btn btn--primary"
              >
                {isChangingRole ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {t('common.saving', 'Saving...')}
                  </>
                ) : (
                  t('users.confirmRoleChange', 'Change Role')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
